/* eslint-disable handle-callback-err */
/**
 * WebSocket Module
 */

import apiEvent from '@/util/call/apiEvent';
import eventBus from '@/EventBus';
import logReport from '../../../util/logReport';
import neomeccaCommon from '@/util/call/neomecca/netDialAPI/ndCommon';
import neoWebRtcCommon from '@/util/call/neoWebRTC/neoWebRtcAPI/ndCommon';

const getDefaultState = () => {
  return {
    telNumber: '',
    callLoginId: '',
    callBrandId: '',
    callAgentGroup: '',
    callBrandList: [], // 일반적인 상황의 콜 브랜드 목록
    callAllBrandList: [], // KT 및 klcns의 경우, CG 할당 브랜드가 아니어도 전화가 꽂힐 수 있는 구조여서, 이를 위해 따로 브랜드 목록 핸들링.
    enabledCallAgent: false,
    isExistCallAgent: undefined,
    stompClient: null,
    isCallConnecting: false,
    isCallSocketConnected: false,
    isValidTel: false,
    isTelIng: false, // 콜 서버 소켓 연결 여부 값
    isOverlap: false,
    stopCnt: 0,
    intervalObj: null, // 네오메카일 때, 소켓 메시지 인입 지연을 감지하기 위해 추가된 변수값
    isIntervalCheck: false,
    pingTryCount: 0,
    ctiAgentGroup: '' // CTI 상담사 대그룹 코드
  };
};
const state = getDefaultState();

const getters = {
  stompClient: (state) => {
    return state.stompClient;
  },
  IS_CALL_CONNECTED: (state) => {
    return state.isCallSocketConnected;
  },
  telNumber: (state) => {
    return state.telNumber;
  },
  callLoginId: (state) => {
    return state.callLoginId;
  },
  callBrandId: (state) => {
    return state.callBrandId;
  },
  callAgentGroup: (state) => {
    return state.callAgentGroup;
  },
  enabledCallAgent: (state) => {
    return state.enabledCallAgent;
  },
  isExistCallAgent: (state) => {
    return state.isExistCallAgent;
  },
  IS_VALID_TEL: (state) => {
    return state.isValidTel;
  },
  IS_TEL_ING: (state) => {
    return state.isTelIng;
  },
  IS_OVER_LAP: (state) => {
    return state.isOverlap;
  },
  isAvailableCall: (state) => {
    return state.enabledCallAgent && state.isExistCallAgent && state.isValidTel && state.isTelIng;
  },
  pingTryCount: (state) => {
    return state.pingTryCount;
  },
  getCallBrandList: (state) => {
    return state.callBrandList;
  },
  getCallBrandById: (state) => (brandId) => {
    return state.callBrandList.find((brand) => brand.brandId === brandId) || {};
  },
  getCallAllBrandList: (state) => {
    return state.callAllBrandList;
  },
  IS_CALL_CONNECTING: (state) => {
    return state.isCallConnecting;
  },
  intervalObj: (state) => {
    return state.intervalObj;
  },
  isIntervalCheck: (state) => {
    return state.isIntervalCheck;
  },
  stopCnt: (state) => {
    return state.stopCnt;
  },
  ctiAgentGroup: (state) => {
    return state.ctiAgentGroup;
  }
};

const mutations = {
  START_CALL_CONNECT(state) {
    state.isCallConnecting = true;
  },
  END_CALL_CONNECT(state) {
    state.isCallConnecting = false;
    state.isCallSocketConnected = true;
  },
  FAIL_CALL_CONNECT(state) {
    state.isCallConnecting = false;
    state.isCallSocketConnected = false;
  },
  OPEN_CALL(state) {
    apiEvent.startMonitor(state.telNumber);
  },
  LOGIN_CALL(state) {
    apiEvent.loginCall(state.callLoginId);
  },
  CLOSE_CALL_SOCKET(state, { isResetState } = { isResetState: true }) {
    if (state.stompClient) {
      state.stompClient.close();
    }
    if (isResetState) {
      Object.assign(state, getDefaultState());
    }
  },
  CLEAR_CALL_DETAILS() {
    apiEvent.removeLocalStorage();
  },
  changeIsExistCallAgent(state, isExistCallAgent) {
    state.isExistCallAgent = isExistCallAgent;
  },
  changeIsValidTel(state, isValidTel) {
    state.isValidTel = isValidTel;
  },
  changeTelNumber(state, telNumber) {
    state.telNumber = telNumber;
  },
  changeEnabledCallAgent(state, payload) {
    state.enabledCallAgent = payload;
  },
  changeIsTelIng(state, isTelIng) {
    state.isTelIng = isTelIng;
  },
  changeIsIntervalCheck(state, isIntervalCheck) {
    state.isIntervalCheck = isIntervalCheck;
  },
  changeIsCallConnecting(state, isCallConnecting) {
    state.isCallConnecting = isCallConnecting;
  },
  increasePingTryCnt(state) {
    state.pingTryCount = ++state.pingTryCount;
  },
  resetPingTryCnt(state) {
    state.pingTryCount = 0;
  },
  setIntervalObj(state, payload) {
    state.intervalObj = payload;
  },
  CLEAR_INTERVAL(state) {
    if (state.intervalObj) {
      clearInterval(state.intervalObj);
      state.stopCnt = 0;
      state.intervalObj = null;
    }
  },
  resetStopCount(state) {
    state.stopCnt = 0;
  },
  addStopCount(state) {
    state.stopCnt += 1;
  },
  setCallBrandList(state, payload) {
    state.callBrandList = payload;
  },
  setCallAllBrandList(state, payload) {
    state.callAllBrandList = payload;
  },
  setCallLoginId(state, payload) {
    state.callLoginId = payload;
  },
  setIsOverlap(state, payload) {
    state.isOverlap = payload;
  },
  setSocketClient(state, payload) {
    state.stompClient = payload;
  },
  setCallBrandId(state, payload) {
    state.callBrandId = payload;
  },
  setCallAgentGroup(state, payload) {
    state.callAgentGroup = payload;
  },
  setIsCallSocketConnected(state, payload) {
    state.isCallSocketConnected = payload;
  },
  setCtiAgentGroup(state, payload) {
    state.ctiAgentGroup = payload;
  }
};

const actions = {
  sendMessageToCallSocket({ state, rootGetters }, payload) {
    const { isNeomecca, isNeoWebRtc } = rootGetters;
    if (state.stompClient) {
      if (state.stompClient.readyState === WebSocket.OPEN) {
        state.stompClient.send(payload);
      } else if (isNeomecca || isNeoWebRtc) {
        eventBus.$emit('websocket-disconnect');
      }
    }
  },
  CLOSE_CALL({ state, commit, rootGetters }, { isResetState } = { isResetState: true }) {
    if (!state.isCallSocketConnected) return;
    state.pingTryCount = 0;
    apiEvent.closeCall(state.callLoginId);
    clearInterval(state.intervalObj);

    const { isKtOrKlcns, isNeomecca, isNeoWebRtc } = rootGetters;

    if (!isKtOrKlcns) {
      // 네오메카일 때, 재로그인 직후 상담사 상태 반영 안되는 이슈와
      // 웹소켓에 반복적으로 상담사 목록 호출 메시지 보내는 현상을 방지하기 위해 코드 추가
      if (isNeomecca) {
        neomeccaCommon.IS_CHANGE_STATUS = false;
      } else if (isNeoWebRtc) {
        neoWebRtcCommon.IS_CHANGE_STATUS = false;
      }
      commit('CLEAR_FETCH_CALL_AGENT_LIST_INTERVAL', { root: true });
    }

    state.stompClient.close();
    if (isKtOrKlcns && isResetState) {
      Object.assign(state, getDefaultState());
    }
    logReport.write('[LOGOUT] EVT:CLOSE_CALL');
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/callWebsocket/index.js